import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Divider,
  Paper,
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import CalculateIcon from 'views/common/components/UI/Icons/CalculateIcon';
import UserCheckIcon from 'views/common/components/UI/Icons/UserCheck';
import gotQs from 'public/imgs/list-your-bike/got_questions.webp';
import PATHS from 'lib/navigation/paths';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';

const PREFIX = 'QuestionsSection';

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  title: `${PREFIX}-title`,
  imgGrid: `${PREFIX}-imgGrid`,
  txtGrid: `${PREFIX}-txtGrid`,
  questionsContainer: `${PREFIX}-questionsContainer`,
  questionIcn: `${PREFIX}-questionIcn`,
  questionTitle: `${PREFIX}-questionTitle`,
  questionDesc: `${PREFIX}-questionDesc`,
  moreAnswers: `${PREFIX}-moreAnswers`,
  imgContainer: `${PREFIX}-imgContainer`,
  img: `${PREFIX}-img`,
  divider: `${PREFIX}-divider`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    marginTop: theme.spacing(13),
    marginBottom: theme.spacing(13),
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(22),
      marginTop: theme.spacing(18),
    },
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(5.5),
      marginBottom: theme.spacing(5.5),
    },
  },

  [`& .${classes.title}`]: {
    fontSize: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(0),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(36),
      marginBottom: theme.spacing(4),
      textAlign: 'left',
    },
    [theme.breakpoints.up('lg')]: {
      textAlign: 'center',
      marginBottom: theme.spacing(13),
    },
  },

  [`& .${classes.imgGrid}`]: {
    [theme.breakpoints.up('lg')]: {
      order: 1,
    },
  },

  [`& .${classes.txtGrid}`]: {
    marginTop: theme.spacing(4.75),
    [theme.breakpoints.up('lg')]: {
      order: 2,
      marginTop: 0,
    },
  },

  [`& .${classes.questionsContainer}`]: {
    padding: theme.spacing(0, 0, 4.5),
    width: 'auto',
    marginLeft: 0,
    [theme.breakpoints.only('sm')]: {
      textAlign: 'center',
    },
    [theme.breakpoints.up('lg')]: {
      marginLeft: '25%',
    },
  },

  [`& .${classes.questionIcn}`]: {
    '& .MuiSvgIcon-root': {
      width: theme.spacing(6),
      height: theme.spacing(6),
      fill: theme.palette.primary.main,

      [theme.breakpoints.only('sm')]: {
        width: theme.spacing(4),
        height: theme.spacing(4),
      },
    },
  },

  [`& .${classes.questionTitle}`]: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: '500',
    [theme.breakpoints.only('sm')]: {
      fontSize: theme.typography.pxToRem(18),
    },
  },

  [`& .${classes.questionDesc}`]: {
    letterSpacing: theme.typography.pxToRem(0.5),
    lineHeight: theme.typography.pxToRem(24),
    marginTop: theme.spacing(1.5),
    fontSize: theme.typography.pxToRem(14),
    '& a': {
      color: theme.palette.secondary.main,
      fontWeight: 'bold',
    },
  },

  [`& .${classes.moreAnswers}`]: {
    padding: 0,
    background: 'none !important',
    textDecoration: 'underline',
    marginLeft: 0,
    [theme.breakpoints.only('sm')]: {
      textAlign: 'center',
      display: 'flex',
    },
    [theme.breakpoints.up('lg')]: {
      marginLeft: '25%',
    },
  },

  [`& .${classes.imgContainer}`]: {
    height: theme.spacing(42.5),
    marginTop: theme.spacing(5.5),
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 20,
    [theme.breakpoints.up('lg')]: {
      height: '100%',
      marginTop: 0,
    },
    [theme.breakpoints.only('sm')]: {
      height: theme.spacing(20),
      marginTop: theme.spacing(2.75),
      borderRadius: 15,
    },
  },

  [`& .${classes.img}`]: {
    minHeight: '100% !important',
    maxWidth: '100% !important',
    position: 'absolute !important',
    margin: '0 !important',
    height: 'auto !important',
    maxHeight: 'none !important',
    width: 'auto !important',
    [theme.breakpoints.up('lg')]: {
      height: '100% !important',
      left: '0 !important',
      maxWidth: 'none !important',
    },
  },

  [`& .${classes.divider}`]: {
    marginTop: theme.spacing(10),
    backgroundColor: theme.palette.grey[200],
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(5.5),
    },
  },
}));

const QuestionsSection = () => {
  const { t } = useTranslation('fe_er_rent_my_motorcycle');
  const appNavigator = useAppNavigator();

  const questionsIcn = [
    <CalculateIcon />,
    <UserCheckIcon />,
    <MonetizationOnIcon />,
  ];

  return (
    <Root>
      <Container maxWidth="xl" classes={{ root: classes.mainContainer }}>
        <Typography variant="h2" classes={{ root: classes.title }}>
          {t('fe_er_rent_my_motorcycle:questions_section_title')}
        </Typography>

        <Grid container alignItems="stretch" spacing={2}>
          <Grid item xs={12} lg={7} classes={{ root: classes.txtGrid }}>
            <Box className={classes.questionsContainer}>
              <Box className={classes.questionIcn}>{questionsIcn[0]}</Box>
              <Typography
                variant="h4"
                classes={{ root: classes.questionTitle }}>
                {t('fe_er_rent_my_motorcycle:questions.q1')}
              </Typography>
              <Typography
                classes={{ root: classes.questionDesc }}
                dangerouslySetInnerHTML={{
                  __html: t('fe_er_rent_my_motorcycle:questions.a1', {
                    er_path: 'https://www.eaglerider.com',
                  }),
                }}
              />
            </Box>
            <Box className={classes.questionsContainer}>
              <Box className={classes.questionIcn}>{questionsIcn[1]}</Box>
              <Typography
                variant="h4"
                classes={{ root: classes.questionTitle }}>
                {t('fe_er_rent_my_motorcycle:questions.q2')}
              </Typography>
              <Typography
                classes={{ root: classes.questionDesc }}
                dangerouslySetInnerHTML={{
                  __html: t('fe_er_rent_my_motorcycle:questions.a2', {
                    club_path: appNavigator.getUrl(
                      undefined,
                      PATHS.motorcycleClub.index().url,
                      true,
                    ),
                    commercial_host_path:
                      'https://www.eagleshare.com/commercial-host',
                  }),
                }}
              />
            </Box>
            <Box className={classes.questionsContainer}>
              <Box className={classes.questionIcn}>{questionsIcn[2]}</Box>
              <Typography
                variant="h4"
                classes={{ root: classes.questionTitle }}>
                {t('fe_er_rent_my_motorcycle:questions.q3')}
              </Typography>

              <Typography
                classes={{ root: classes.questionDesc }}
                dangerouslySetInnerHTML={{
                  __html: t('fe_er_rent_my_motorcycle:questions.a3', {
                    club_path: appNavigator.getUrl(
                      undefined,
                      PATHS.motorcycleClub.index().url,
                      true,
                    ),
                    commercial_host_path:
                      'https://www.eagleshare.com/commercial-host',
                  }),
                }}
              />
            </Box>
            <Button
              color="primary"
              classes={{ root: classes.moreAnswers }}
              target="_blank"
              href="https://support.eaglerider.com/frequently-asked-questions#eagleshare-private-owner"
              endIcon={<ArrowForwardIcon />}>
              {t('fe_er_rent_my_motorcycle:find_more_answers')}
            </Button>
          </Grid>
          <Grid item xs={12} lg={5} classes={{ root: classes.imgGrid }}>
            <Box className={classes.imgContainer}>
              <Image
                className={classes.img}
                src={gotQs}
                alt={t('fe_er_rent_my_motorcycle:title')}
                layout="fill"
                unoptimized
                placeholder="blur"
              />
            </Box>
          </Grid>
        </Grid>

        <Paper sx={{ display: { md: 'block', lg: 'none' } }}>
          <Divider classes={{ root: classes.divider }} />
        </Paper>
      </Container>
    </Root>
  );
};

export default QuestionsSection;
